import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import { ButtonType } from 'components/Button/Button';
import ActionCard from 'components/Common/Cards/ActionCard';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';
import PoweredByEquifax, {
  PoweredByEquifaxTone,
} from 'components/FinancialCheckup/FinancialDashboard/PoweredByEquifax';
import {
  AcceleratePrimaryAction,
  AccelerateYourGoalsCardContent,
} from 'components/FinancialCheckup/FinancialDashboard/AccelerateYourGoalsSection/resolveAccelerateYourGoalsCard';
import { HardOfferData } from 'handlers/applicationData';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanOfferData } from 'selectors/getLoanOfferData';
import { getStudentLoanData } from 'selectors/getStudentLoanData';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';
import { FlowComponentType } from 'routes/types';
import { useNavigate } from 'hooks/useNavigate';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import {
  getApplicationData as getApplicationDataThunk,
  getGoalProgress,
  getNextSteps,
  startScoreImprovementPlan,
} from 'thunks';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { getLocalStorageItem } from 'utils/getLocalStorageItem';
import { LocalStorageKeyName } from 'enums/LocalStorageKeyName';
import { LoanOfferResponse } from 'handlers/loanOffer';
import { ReactComponent as WalletIcon } from 'images/repayment-items/wallet-icon.svg';
import { ReactComponent as DownwardTrendIcon } from 'images/downward-trend-icon.svg';

import accelerateStyles from 'components/FinancialCheckup/FinancialDashboard/AccelerateYourGoalsSection/AccelerateYourGoalsSection.module.scss';
import commonStyles from 'components/StudentLoanForgiveness/Common/Common.module.scss';

const hasPositiveOfferSavings = (hardOffer: Partial<HardOfferData>): boolean => {
  const { moneySaved, monthsSaved, monthlyPaymentSaved } = hardOffer.offerSummary ?? {};
  return (moneySaved ?? 0) > 0 || (monthsSaved ?? 0) > 0 || (monthlyPaymentSaved ?? 0) > 0;
};

const storedLoanOfferStatus = (): ApplicationStatusName | undefined => {
  try {
    const stored = getLocalStorageItem(LocalStorageKeyName.LoanOfferResult) as LoanOfferResponse | null;
    return stored?.data?.application_status;
  } catch {
    return undefined;
  }
};

const hasDebtOffer = (
  applicationStatus: ApplicationStatusName | undefined,
  loanOfferStatus: ApplicationStatusName | undefined,
  hardOffer: Partial<HardOfferData>,
): boolean =>
  applicationStatus === ApplicationStatusName.OfferAvailable ||
  loanOfferStatus === ApplicationStatusName.OfferAvailable ||
  hasPositiveOfferSavings(hardOffer);

const resolveExploreMoreCard = (
  showDebtOffer: boolean,
  hardOffer: Partial<HardOfferData>,
  scoreImprovement?: number,
): AccelerateYourGoalsCardContent => {
  if (showDebtOffer) {
    const monthlyPaymentSaved = hardOffer.offerSummary?.monthlyPaymentSaved;
    const bullets = ['Consolidate debt with Plannery'];
    if (monthlyPaymentSaved && monthlyPaymentSaved > 0) {
      bullets.push(`Save ${formatMonetaryAmount(monthlyPaymentSaved)}/mo on debt payments`);
    }
    bullets.push('Free up cash for other goals');

    return {
      title: 'Pay off debt sooner with Plannery',
      bullets,
      primaryButtonText: 'SEE MY PLAN',
      primaryAction: AcceleratePrimaryAction.SeeMyPlan,
    };
  }

  return {
    title: 'Plannery ScoreIQ©',
    showScoreIQAttribution: true,
    primaryButtonText: 'ENROLL IN SCOREIQ',
    primaryAction: AcceleratePrimaryAction.EnrollScoreIQ,
    subtitle: scoreImprovement
      ? `Boost your credit score by ${scoreImprovement} points and you could qualify to pay off existing debt faster with Plannery`
      : undefined,
    bullets: scoreImprovement
      ? undefined
      : [
          'Boost your credit score to improve your finances',
          'ScoreIQ can give you personalized guidance to improve your score',
        ],
  };
};

const ExploreMore = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const { application } = useSelector(getApplicationData);
  const loanOffer = useSelector(getLoanOfferData);
  const { applicationId: studentLoanApplicationId } = useSelector(getStudentLoanData);
  const [isEnrolling, setIsEnrolling] = useState(false);

  const applicationId = application?.id ?? studentLoanApplicationId ?? loanOffer.data.application_id ?? undefined;

  // Always refresh: Analyzing may leave a stale Manual Review on applicationData while loanOffer
  // already has Offer Available. Do not block the UI on this request.
  useEffect(() => {
    if (!applicationId) {
      return;
    }
    dispatchWithUnwrap(getApplicationDataThunk(applicationId)).catch((error: unknown) => {
      analytics.track('Failed to refresh application for Explore more', { error, applicationId });
    });
  }, [applicationId, dispatchWithUnwrap]);

  const hardOffer = (application?.hardOffer ?? {}) as Partial<HardOfferData>;
  const showDebtOffer = hasDebtOffer(
    application?.status,
    loanOffer.data.application_status ?? storedLoanOfferStatus(),
    hardOffer,
  );
  const cardContent = resolveExploreMoreCard(showDebtOffer, hardOffer, application?.optimalPathPlan?.scoreImprovement);
  const Icon = cardContent.primaryAction === AcceleratePrimaryAction.SeeMyPlan ? WalletIcon : DownwardTrendIcon;

  const handlePrimaryClick = async () => {
    if (!applicationId) return;

    if (cardContent.primaryAction === AcceleratePrimaryAction.EnrollScoreIQ) {
      if (!application) return;
      setIsEnrolling(true);
      try {
        await dispatchWithUnwrap(startScoreImprovementPlan(application.id));
        if (application.borrowerId) {
          dispatch(getGoalProgress(application.borrowerId));
          dispatch(getNextSteps(application.borrowerId));
        }
      } catch (error) {
        analytics.track('Failed to start score improvement plan', { error });
      } finally {
        setIsEnrolling(false);
      }
      return;
    }

    navigate(RoutePath.OfferAvailable, OFFER_AVAILABLE_FROM_DASHBOARD_STATE);
  };

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        <FormContainer title="Explore more savings" subtitle="See what else Plannery can do for you.">
          <ActionCard>
            <ActionCard.Row className={accelerateStyles.titleRow}>
              <div className={accelerateStyles.iconCircle}>
                <Icon />
              </div>
              <div className={accelerateStyles.titleBlock}>
                <ActionCard.Title className={accelerateStyles.title}>
                  {cardContent.title.endsWith('©') ? (
                    <>
                      {cardContent.title.slice(0, -1)}
                      <sup className={accelerateStyles.copyright}>©</sup>
                    </>
                  ) : (
                    cardContent.title
                  )}
                </ActionCard.Title>
                {cardContent.showScoreIQAttribution && <PoweredByEquifax tone={PoweredByEquifaxTone.OnDark} />}
              </div>
            </ActionCard.Row>
            {cardContent.subtitle && <ActionCard.Description>{cardContent.subtitle}</ActionCard.Description>}
            {cardContent.bullets && cardContent.bullets.length > 0 && (
              <div className={accelerateStyles.bulletsContainer}>
                <ol className={accelerateStyles.bullets}>
                  {cardContent.bullets.map((bullet) => (
                    <li key={bullet} className={accelerateStyles.bullet}>
                      {bullet}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <Button type={ButtonType.Inverted} onClick={handlePrimaryClick} isLoading={isEnrolling}>
              {cardContent.primaryButtonText}
            </Button>
          </ActionCard>

          <div className={commonStyles.buttons}>
            <Button onClick={() => handleNext()}>Go to my dashboard</Button>
          </div>
        </FormContainer>
      </>
    </StudentLoanLoaderWrapper>
  );
};

export default ExploreMore;
